import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MdPersonAdd, MdKeyboardVoice, MdPhotoCamera, MdVideocam, MdNotificationsNone } from "react-icons/md";
import { BsFillFileTextFill } from "react-icons/bs";

import { getNotifications, readNotifications } from "../../api";
import { getMediaUrl } from "../../lib/utils";
import { useUserContext } from "../../context/AuthContext";
import NotificationPostModal from "../../components/NotificationPostModal";

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNotification, setSelectedNotification] = useState(null);

  const { authUser } = useUserContext();
  const navigate = useNavigate();

  /* ================= GET NOTIFICATIONS ================= */

  useEffect(() => {
    let mounted = true;

    const fetchNotifications = async () => {
      try {
        const res = await getNotifications();

        if (!mounted) return;

        setNotifications(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchNotifications();

    return () => {
      mounted = false;
    };
  }, []);

  /* ================= POST TYPE ICON ================= */

  const getPostTypeIcon = (postType) => {
    switch (postType) {
      case "text":
        return <BsFillFileTextFill className="h-5 w-5 text-purple-600" />;

      case "photo":
        return <MdPhotoCamera className="h-6 w-6 text-emerald-600" />;

      case "video":
        return <MdVideocam className="h-6 w-6 text-blue-600" />;

      case "voice":
        return <MdKeyboardVoice className="h-6 w-6 text-orange-500" />;

      default:
        return <MdNotificationsNone className="h-5 w-5 text-gray-400" />;
    }
  };

  /* ================= NOTIFICATION ICON ================= */

  const getNotificationIcon = (notification) => {
    switch (notification?.type) {
      case "follow":
      case "follow_back":
        return <MdPersonAdd className="h-6 w-6 text-purple-600" />;

      default:
        return getPostTypeIcon(notification?.post_type);
    }
  };

  /* ================= ACTIVITY ================= */

  const getActivityText = (notification) => {
    if (notification?.activity) return notification.activity;

    switch (notification?.type) {
      case "like":
        return "liked your post";

      case "comment":
        return "commented on your post";

      case "comment_like":
        return "liked your comment";

      case "reply":
        return "replied to your comment";

      case "follow":
        return "started following you";

      case "follow_back":
        return "followed you back";

      case "new_post":
        return "shared a new post";

      default:
        return "interacted with you";
    }
  };

  /* ================= MARK AS READ ================= */

  const handleMarkAsRead = async (id) => {
    try {
      await readNotifications(id);

      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === id ? { ...notification, seen: 1 } : notification
        )
      );
    } catch (error) {
      console.error("Unable to mark notification as read:", error);
    }
  };

  /* ================= NOTIFICATION CLICK ================= */

  const handleNotificationClick = async (notification) => {
    if (!notification.seen) await handleMarkAsRead(notification.id);

    if (["like", "comment", "comment_like", "reply", "new_post"].includes(notification.type) && notification.post_id) {
      setSelectedNotification(notification);
      return;
    }

    if (["follow", "follow_back"].includes(notification.type) && notification.user_id) {
      navigate(`/profile/${notification.user_id}`);
    }
  };

  /* ================= PROFILE CLICK ================= */

  const handleProfileClick = async (event, notification) => {
    event.stopPropagation();

    if (!notification.seen) await handleMarkAsRead(notification.id);
  };

  /* ================= KEYBOARD ================= */

  const handleNotificationKeyDown = (event, notification) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleNotificationClick(notification);
    }
  };

  /* ================= LOADING ================= */

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6">
        <div className="mb-6 h-8 w-48 animate-pulse rounded-lg bg-gray-200" />

        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          {[1, 2, 3, 4, 5].map((item) => (
            <div key={item} className="flex items-center gap-4 border-b border-gray-100 p-4 last:border-b-0">
              <div className="h-12 w-12 shrink-0 animate-pulse rounded-full bg-gray-200" />

              <div className="flex-1">
                <div className="mb-2 h-4 w-2/3 animate-pulse rounded bg-gray-200" />
                <div className="h-3 w-32 animate-pulse rounded bg-gray-100" />
              </div>

              <div className="h-10 w-10 animate-pulse rounded-xl bg-gray-100" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const unreadCount = notifications.filter((notification) => !notification.seen).length;

  return (
    <>
      <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6">

        {/* ================= HEADER ================= */}

        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Notifications</h1>

            <p className="mt-1 text-sm text-gray-500">
              {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount === 1 ? "" : "s"}` : "You're all caught up."}
            </p>
          </div>

          {unreadCount > 0 && (
            <span className="rounded-full bg-purple-50 px-3 py-1.5 text-xs font-semibold text-purple-600">
              {unreadCount} new
            </span>
          )}
        </div>

        {/* ================= EMPTY ================= */}

        {!notifications.length ? (
          <div className="rounded-2xl border border-gray-100 bg-white px-6 py-20 text-center shadow-sm">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-purple-50">
              <MdNotificationsNone size={30} className="text-purple-500" />
            </div>

            <h2 className="mt-4 text-lg font-semibold text-gray-800">No notifications yet</h2>

            <p className="mx-auto mt-1 max-w-sm text-sm leading-6 text-gray-500">
              When someone interacts with you or someone you follow shares a new post, you'll see it here.
            </p>
          </div>
        ) : (

          /* ================= LIST ================= */

          <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                role="button"
                tabIndex={0}
                onClick={() => handleNotificationClick(notification)}
                onKeyDown={(event) => handleNotificationKeyDown(event, notification)}
                className={`relative flex cursor-pointer items-center gap-3 border-b border-gray-100 p-4 text-left transition last:border-b-0 sm:gap-4 sm:p-5 ${notification.seen ? "bg-white hover:bg-gray-50" : "bg-purple-50/50 hover:bg-purple-50"}`}
              >

                {/* ================= USER PROFILE IMAGE ================= */}

                <Link
                  to={`/profile/${notification.user_id}`}
                  onClick={(event) => handleProfileClick(event, notification)}
                  className="shrink-0"
                >
                  <img
                    src={getMediaUrl(notification.profile_img)}
                    alt={`${notification.firstname} ${notification.lastname}`}
                    className="h-12 w-12 rounded-full object-cover ring-2 ring-white transition hover:ring-purple-300 sm:h-14 sm:w-14"
                  />
                </Link>

                {/* ================= CONTENT ================= */}

                <div className="min-w-0 flex-1">
                  <p className="text-sm leading-6 text-gray-600 sm:text-[15px]">

                    {/* USER NAME */}

                    <Link
                      to={`/profile/${notification.user_id}`}
                      onClick={(event) => handleProfileClick(event, notification)}
                      className="font-semibold text-gray-900 hover:text-purple-600 hover:underline"
                    >
                      {notification.firstname} {notification.lastname}
                    </Link>{" "}

                    {/* ACTIVITY */}

                    <span className="transition group-hover:text-purple-600">
                      {getActivityText(notification)}
                    </span>
                  </p>

                  <p className="mt-1 text-xs text-gray-400">
                    {new Date(notification.created_at).toLocaleString()}
                  </p>
                </div>

                {/* ================= ACTION ICON ================= */}

                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-50 transition group-hover:bg-purple-50 sm:h-11 sm:w-11">
                  {getNotificationIcon(notification)}
                </div>

                {/* ================= UNREAD ================= */}

                {!notification.seen && (
                  <span className="absolute right-2 top-2 h-2.5 w-2.5 rounded-full bg-purple-600 sm:right-3 sm:top-3" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ================= POST MODAL ================= */}

      <NotificationPostModal
        notification={selectedNotification}
        user={authUser}
        onClose={() => setSelectedNotification(null)}
      />
    </>
  );
};

export default Notifications;